import { LOGIN_URL, MAIN_SITE_HOMEPAGE, PUBLIC_URL } from 'config';
import { jwtDecode } from 'jwt-decode';
import { batch } from 'react-redux';
import { RequestConfig } from 'services/api/api.types';
import { withCurrentStore } from 'store/currentStore';
import { clearCookie, getCookieValue, setCookie } from 'utils/functions/cookie';
import { createScopedErrorHandler } from 'utils/functions/handleError';
import { v4 as uuidV4 } from 'uuid';
import { endUserSession } from './session.actions';
import {
  authTokenKey,
  cancelCallbackUrlKey,
  endCallbackUrlKey,
} from './session.config';
import { selectSessionInfo } from './session.selectors';

export const sessionErrorHandler = createScopedErrorHandler('session');

/**
 * Computes 'siteid' from the current navigator instance.
 * @param navigator 'window.navigator'
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator
 */
export function computeSiteIdFromNavigator(navigator: Navigator) {
  const searchString = navigator.userAgent || navigator.vendor;

  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      searchString,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      searchString.slice(0, 4),
    )
  ) {
    return true;
  }
  return false;
}

export const devOnlySetupEnvironment = () => {
  const searchParams = new URLSearchParams(window.location.search);

  if (searchParams.get('skip-dev-setup') !== '1') {
    window.localStorage.setItem(
      authTokenKey,
      import.meta.env.VITE_APP_DEV_ONLY_AUTH_TOKEN || '',
    );
    window.sessionStorage.setItem(
      SESSION_ID_KEY,
      import.meta.env.VITE_APP_DEV_ONLY_SESSION_ID || '',
    );
  }
};

export function isAnAuthenticatedApiRequest(
  requestConfig: RequestConfig,
): boolean {
  const headers: Record<string, any> | undefined = requestConfig.headers;

  if (!headers) {
    return false;
  }

  const keys = Object.keys(headers);

  for (let i = 0, len = keys.length; i < len; i++) {
    if (keys[i].toLowerCase() === 'authorization') {
      return true;
    }
  }

  return false;
}

export function createEndcallbackUrl(): string {
  try {
    const parsedURL: URL = new URL(PUBLIC_URL);

    if (window.location.search) {
      const currentPageSearchParams = new URLSearchParams(
        window.location.search,
      );

      const parsedURLSearchParams = parsedURL.searchParams;

      // Do not pass the original token, if present.
      currentPageSearchParams.delete(authTokenKey);

      currentPageSearchParams.forEach((value, key) => {
        parsedURLSearchParams.set(key, value);
      });
    }

    if (window.location.pathname) {
      parsedURL.pathname = window.location.pathname;
    }

    if (window.location.hash) {
      parsedURL.hash = window.location.hash;
    }

    return parsedURL.href;
  } catch (sessionError) {
    sessionErrorHandler('createEndcallbackUrl error', sessionError);

    return PUBLIC_URL;
  }
}

export function create401RedirectUrl(): string {
  try {
    // @see https://developer.mozilla.org/en-US/docs/Web/API/URL
    const parsedURL = new URL(LOGIN_URL);
    const endCallbackUrlFromLandingPage = withCurrentStore(
      (store) => selectSessionInfo(store.getState()).endCallbackUrl,
    );

    // As per requirements pass 'endcallbackurl' from landing if present
    // and valid or fallback to the current page.
    const endCallbackUrl =
      endCallbackUrlFromLandingPage ?? createEndcallbackUrl();

    const originalSearchParams = parsedURL.searchParams;

    originalSearchParams.set(cancelCallbackUrlKey, MAIN_SITE_HOMEPAGE);

    originalSearchParams.set(endCallbackUrlKey, endCallbackUrl);

    if (getAuthType() === AuthType.Sso) {
      originalSearchParams.set('auth-type', AuthType.Sso);
    }
    return parsedURL.href;
  } catch (sessionError) {
    sessionErrorHandler('create401RedirectUrl error', sessionError);
    // We fallback to the original url in case of exception.
    return LOGIN_URL;
  }
}

export function trigger401Redirect(): void {
  const redirectUrl = create401RedirectUrl();

  if (!import.meta.env.PROD) {
    console.info(`[client-side-redirect][401]: ${redirectUrl}`);
  }

  window.localStorage.removeItem('poa-last-upload');
  window.localStorage.removeItem('poo-last-upload');

  window.location.href = redirectUrl;
}

/**
 * Extracts 'endcallbackurl' from query params.
 * @param searchParams @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 */
export function acquireEndcallbackUrlFromQueryParams(
  searchParams: URLSearchParams,
): string | null {
  const endCallbackUrlToValidate: string | null =
    searchParams.get(endCallbackUrlKey);

  if (!endCallbackUrlToValidate || endCallbackUrlToValidate.length < 3) {
    return null;
  }

  let parsedURL: URL | null = null;

  try {
    parsedURL = new URL(endCallbackUrlToValidate);
  } catch (InvalidURLError) {
    sessionErrorHandler(
      `invalid ${endCallbackUrlKey} provided, got "${endCallbackUrlToValidate}"`,
      InvalidURLError,
    );
  }

  if (parsedURL) {
    return parsedURL.toString();
  }

  return null;
}

/**
 * Meant to be invoked after a logout request.
 * Performs the following actions:
 *
 * 1. Removes the token from storage.
 * 2. Resets the store.
 * 3. Redirects user to the the main site HP.
 */
export function cleanUpAndEndUserSession() {
  withCurrentStore((reduxStore) => {
    batch(() => {
      // Try to remove the token from the local storage.
      try {
        window.localStorage.removeItem('poa-last-upload');
        window.localStorage.removeItem('poo-last-upload');
        window.localStorage.removeItem(authTokenKey);
        clearCookie('JWT');
        clearCookie('JWT_ar');
        clearCookie('login');
        clearCookie('login_fr');
      } catch (logoutError) {
        sessionErrorHandler(logoutError);
      }

      // Clear redux store.
      try {
        reduxStore.dispatch(endUserSession());
      } catch (logoutError) {
        sessionErrorHandler(logoutError);
      }

      let logoutUrl = MAIN_SITE_HOMEPAGE;

      // Add `action=logout` query param
      try {
        const parsedLogoutUrl = new URL(logoutUrl);

        parsedLogoutUrl.searchParams.set('action', 'logout');

        logoutUrl = parsedLogoutUrl.href;
      } catch (logoutError) {
        sessionErrorHandler(logoutError);
      }

      // Redirects to main site HP.
      window.location.href = logoutUrl;
    });
  });
}

export function cleanAuthTokenQueryParams() {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete(authTokenKey);

  window.history.replaceState({}, document.title, currentUrl.toString());
}

export const AuthType = {
  Classic: 'classic',
  Sso: 'sso',
} as const;

type TAuthType = (typeof AuthType)[keyof typeof AuthType];

/**
 * Infers the authentication type from the provided auth token.
 * The stateful auth tokens are not valid JWT tokens, so if the decoding fails
 * it's assumed to be a classic auth type.
 *
 * @param authToken - The authentication token to infer the type from.
 * @returns The inferred authentication type.
 */
export function inferAuthTypeFromAuthToken(authToken: string): TAuthType {
  try {
    jwtDecode(authToken);
    return AuthType.Sso;
  } catch (error) {
    console.debug("Failed to decode JWT token, assuming 'classic' auth type");
    return AuthType.Classic;
  }
}

export function setAuthType(authType: TAuthType) {
  setCookie({
    name: 'auth-type',
    value: authType,
    domain: window.location.hostname,
  });
}

export function getAuthType(): TAuthType {
  const authType = getCookieValue('auth-type');

  switch (authType) {
    case AuthType.Classic:
      return AuthType.Classic;
    default:
      return AuthType.Sso;
  }
}

export const SESSION_ID_KEY = 'session-id';

export function maybeSetSessionId() {
  const currentSessionId = getSessionId();
  if (currentSessionId) return;

  const sessionId = uuidV4();
  window.sessionStorage.setItem(SESSION_ID_KEY, sessionId);
}

export function getSessionId() {
  return window.sessionStorage.getItem(SESSION_ID_KEY) ?? '';
}
